import React, {useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {DateTime} from 'luxon';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowDown} from '@fortawesome/pro-solid-svg-icons';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import {SectionTriangle} from 'components/Elements/Section';
import Laurels from 'components/Laurels';
import FilmOptinForm from 'components/Forms/FilmOptin';
import FilmOptinModal, {FilmOptinModalTrigger} from 'components/Modals/FilmOptinModal';
import Video from 'components/Video';
import CountdownTimer from 'components/Elements/CountdownTimer';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';

import {reviews, discover, discoverAd} from 'data/fungi.json';
import orids from 'data/orids.json';

export default function LandingIndex({
  noExitIntent = false,
  noFixedVideo = true,
  videoUrlOverride = null,
  variant = null,
  ...props
}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: ''
      },
      special: specialSchedule = {}
    }
  } = usePhaseState();

  const phase = specialSchedule.phase ? specialSchedule.phase : defaultSchedule.phase;
  const [partnerName, partnerLogo] = (specialSchedule.orid && orids[specialSchedule.orid]) || [];

  const toOrdinalSuffix = (num) => {
    const int = parseInt(num),
      digits = [int % 10, int % 100],
      ordinals = ['st', 'nd', 'rd', 'th'],
      oPattern = [1, 2, 3, 4],
      tPattern = [11, 12, 13, 14, 15, 16, 17, 18, 19];
    return oPattern.includes(digits[0]) && !tPattern.includes(digits[1])
      ? int + ordinals[digits[0] - 1]
      : int + ordinals[3];
  };

  const launchStart = specialSchedule.startTime
    ? DateTime.fromMillis(specialSchedule.startTime)
    : DateTime.fromISO(defaultSchedule.launchStart);
  const launchStartNum = launchStart.setZone('America/Los_Angeles').toFormat('d');

  const launchEnd = specialSchedule.endTime
    ? DateTime.fromMillis(specialSchedule.endTime)
    : DateTime.fromISO(defaultSchedule.launchEnd);
  const launchEndNum = launchEnd.setZone('America/Los_Angeles').toFormat('d');

  function Timer({className = null, ...props}) {
    return (
      <>
        {phase === 'during' ? (
          <>
            <div className={classNames('section-countdown text-center mt-4', className)}>
              <Row className="d-flex align-items-center justify-content-center m-0">
                <Col xs="12">
                  <h3 className="section-heading mb-3">Your free viewing expires in:</h3>
                </Col>
                <Col xs="12" className="timer">
                  <CountdownTimer time={launchEnd} />
                </Col>
              </Row>
            </div>
          </>
        ) : null}
      </>
    );
  }

  function DiscoverBox({image, heading, text, ...props}) {
    return (
      <Col lg="6" className="d-flex flex-column mb-4">
        <div className="background-white box-shadow-black-25 text-center d-flex flex-column flex-grow-1">
          <div
            className="discover-image background-cover background-image-center"
            style={{backgroundImage: `url(${image})`}}
          />
          <div className="p-4 d-flex flex-column flex-grow-1">
            <h4 className="section-heading mb-3">{heading}</h4>
            <p className="my-auto">{text}</p>
          </div>
        </div>
      </Col>
    );
  }

  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta />
      <div className="page-content">
        <SectionTriangle
          id="ff-landing-header"
          color="black"
          className="section-triangle-first"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-header-backdrop-landon-parenteau-EEuDMqRYbx0-unsplash.jpg')}
          offset="bottom"
        >
          <Container className="px-mobile-none">
            <Row className="text-center text-white text-shadow-dark mb-4 px-mobile">
              <Col>
                {phase === 'during' ? (
                  <>
                    <h5 className="section-heading mb-2">A FREE SPECIAL EVENT SCREENING UNTIL:</h5>
                    <CountdownTimer time={launchEnd} />
                  </>
                ) : (
                  <h5 className="section-only-heading">A FREE 4-DAY SPECIAL SCREENING EVENT</h5>
                )}
              </Col>
            </Row>
            <div className="background-white-80 text-center p-4">
              <Laurels />
            </div>
            <div className="background-ff-orange text-center text-white p-4 d-flex align-items-center justify-content-center">
              <div className="px-3">
                <FontAwesomeIcon icon={faArrowDown} className="text-h5" />
              </div>
              <div>
                <h4 className="section-only-heading text-accent text-uppercase">Watch the Trailer</h4>
              </div>
              <div className="px-3">
                <FontAwesomeIcon icon={faArrowDown} className="text-h5" />
              </div>
            </div>
            <Video url="https://www.youtube.com/watch?v=bWax2eIU73Y" label="FF - Film Trailer" />
          </Container>
        </SectionTriangle>

        <SectionTriangle id="ff-optin" color="ff-muted-orange" type="both">
          <Container className="px-mobile-none">
            <div className="optin-box background-ff-gray-80 text-white p-4 p-lg-5">
              <h4 className="section-heading text-center mb-5">
                Enter Your Email Now to Watch The <i>Fantastic Fungi Special Event Edition</i>
              </h4>
              <FilmOptinForm linkColor="white" />
            </div>
            <Timer className="text-white" />
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="ff-landing-testimonials"
          color="black"
          imgUrl={require('static/backgrounds/ff-landing-image-body-backdrop-ashleigh-shea-otVUcXqwqGM-unsplash.jpg')}
          offset="bottom"
          className="background-overlay-ff-muted-orange-40"
        >
          <Container>
            <Row className="align-items-stretch justify-content-center text-center">
              {reviews.map((review, i) => (
                <Col lg="4" className="d-flex flex-column flex-grow-1 mb-4">
                  <div className="d-flex flex-column flex-grow-1 background-white box-shadow-black-50 p-4">
                    <div className="my-auto">
                      <h5 className="section-heading">{review.text}</h5>
                      <p className="text-ff-muted-orange text-accent text-small mb-0">— {review.name}</p>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="ff-landing-story" color="ff-gray" type="both">
          <Container>
            <Row>
              <Col>
                <h2 className="section-only-heading text-center text-white">
                  {variant === 'ad' ? (
                    <>
                      A story of innovators and disruptors, chefs and foragers, mental health, and healing the planet.
                    </>
                  ) : (
                    <>
                      A story of innovators and disruptors, chefs and foragers, healing mental health, and healing the
                      planet.
                    </>
                  )}
                </h2>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="ff-recipe-1"
          color="black"
          imgUrl={require('static/backgrounds/ff-landing-image-body-recipe-alexandr-dzyuba-EXE_dw4bP1M-unsplash.jpg')}
          bgImgPosition="center-bottom"
          offset="bottom"
        />

        <SectionTriangle id="ff-recipe-2" color="white" type="both" innerClass="">
          <Container>
            <Row className="align-items-center justify-content-center">
              <Col md="10" lg="6" className="order-lg-2 mb-5 mb-lg-0">
                <img
                  className=""
                  src="https://cdn.foodrevolution.org/ff/mouthwatering-mushrooms-cover.png"
                  style={{marginTop: '-180px'}}
                />
              </Col>
              <Col lg="6" className="order-lg-1 text-center text-lg-left">
                <h3 className="section-heading text-accent mb-3">
                  In celebration of this special screening event, get a curated recipe guide with 20 of our favorite
                  whole foods plant-based mushroom dishes from
                </h3>
                <img src="https://cdn.foodrevolution.org/logo/frn-logo.svg" style={{maxHeight: '75px'}} />
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="ff-landing-discover" color="ff-cream" type="bottom">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center mb-5">
                  What You’ll Discover in the Special Event Edition of <i>Fantastic Fungi</i>
                </h3>
              </Col>
            </Row>
            <Row className="align-items-stretch justify-content-center">
              {variant === 'ad'
                ? discoverAd.map((discover, i) => (
                    <DiscoverBox image={discover.image} heading={discover.heading} text={discover.text} />
                  ))
                : discover.map((discover, i) => (
                    <DiscoverBox image={discover.image} heading={discover.heading} text={discover.text} />
                  ))}
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="ff-landing-special"
          color="white"
          type="bottom"
          imgUrl={require('static/backgrounds/ff-landing-image-body-asyouwatch-forfeathering-elliott-stallion-Tz_ikM242f4-unsplash.jpg')}
          imgPosition="left"
        >
          {variant === 'ad' ? (
            <>
              <h5 className="section-heading">
                There’s life <i>before</i> watching the special event edition of <i>Fantastic Fungi…</i> And life{' '}
                <i>after.</i>
              </h5>

              <p>
                Be prepared for a thrilling ride that includes gorgeous timelapse photography to give you chills,
                instill you with a newfound sense of wonder, and fill you with hope for the future.
              </p>
              <p>
                Fungi are the largest, most powerful organism on Earth. They’ve also been the most underappreciated —
                until now.
              </p>
              <p>
                Thanks to the critically acclaimed film, <i>Fantastic Fungi,</i> you’re invited into a fascinating realm
                that’s been literally hidden underground for most of human existence.
              </p>
              <h5 className="mt-0">
                As you watch the special event edition of <i>Fantastic Fungi</i> film, you’ll get to witness an
                incredibly moving and inspiring story — a story of evolving consciousness and connection.
              </h5>
              <p>Fungi have the power to steer our future in the right direction.</p>
              <p>
                But unless we learn how to understand the language of fungi, not only will we destroy these organisms,
                but we will destroy ourselves.
              </p>
              <p>This film is a MUST-SEE for anyone who cares about the fate of our planet.</p>
            </>
          ) : (
            <>
              <h5 className="section-heading">
                As you watch this special event edition of <i>Fantastic Fungi</i> film, you’ll get to witness an
                incredibly moving and inspiring story — a story of evolving consciousness, healing, and connection.
              </h5>
              <p>Fungi have the power to steer our future in the right direction.</p>
              <p>
                But for years, scientists were barred from studying certain kinds of mushrooms. Now, new research shows
                promise for depression, anxiety, PTSD, and more.
              </p>
              <p>
                Unless we learn how to understand the language of fungi, not only will we destroy these organisms, but
                we will destroy ourselves.
              </p>
              <p>This film is a MUST-SEE for anyone who cares about the fate of humanity, and of our planet.</p>
            </>
          )}
        </SectionTriangle>

        <SectionTriangle
          id="ff-landing-cta"
          color="black"
          imgUrl={require('static/backgrounds/ff-landing-image-body-backdrop-elliott-stallion-Tz_ikM242f4-unsplash.jpg')}
          className="text-white text-center text-shadow-dark background-overlay-black-50"
          offset="bottom"
        >
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading mb-2">
                  For just a few days, you can join the special event screening and watch the special edition of{' '}
                  <i>Fantastic Fungi</i>
                </h3>
                <h2 className="section-heading text-h1 text-uppercase">for free</h2>
                <FilmOptinModalTrigger />
                <Timer />
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="ff-landing-credit" color="ff-gray" type="top">
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading text-center text-white mb-4">Free Screening Brought To You By:</h4>
              </Col>
            </Row>
            <Row className="align-items-center justify-content-center text-center">
              <Col xs="12" lg="auto" className="mb-4 mb-lg-0">
                <img
                  className="w-100"
                  src="https://cdn.foodrevolution.org/logo/frn-logo-white.svg"
                  style={{maxHeight: '150px'}}
                />
              </Col>
              <Col xs="12" lg="auto">
                <img src="https://cdn.foodrevolution.org/ff/ff-logo-white.png" style={{maxHeight: '100px'}} />
              </Col>
            </Row>
          </Container>
        </SectionTriangle>
      </div>

      {noExitIntent ? null : <ModalExitLanding />}

      <div className="footer-content mt-auto">
        <Footer partner />
        <ShareButtons floating />
      </div>
      <FilmOptinModal />
    </div>
  );
}
